import { Component, inject, Input, OnInit } from "@angular/core";
import { CommonModule, NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";

import { finalize, Observable, tap } from "rxjs";

import { PlanTableService } from "@shared/components/plan-table/services/plan-table.service";
import { LoadingComponent, RefreshComponent, ButtonComponent } from "@shared/components";
import { PlanTableRecord, PlanTableResponse, RequestState } from "@shared/models";
import { DateIntToStringPipe, MinutesToTimePipe } from "@shared/pipes";
import { convertToMinutes, formatTimeInput } from "@shared/functions";
import { fadeInOutAnimation } from "@shared/animations";
import { GlobalService } from "@shared/services";
import { CacheId } from "@shared/enums";

@Component({
  selector: "app-plan-table",
  standalone: true,
  imports: [
    CommonModule,
    LoadingComponent,
    RefreshComponent,
    ButtonComponent,
    NgClass,
    FormsModule,
    MinutesToTimePipe,
    DateIntToStringPipe,
  ],
  templateUrl: "./plan-table.component.html",
  styleUrl: "./plan-table.component.scss",
  animations: [fadeInOutAnimation],
})
export class PlanTableComponent implements OnInit {
  globalService = inject(GlobalService);
  planTableService = inject(PlanTableService);
  router = inject(Router);
  @Input() planKey: number = 0;

  plan$: Observable<RequestState<PlanTableResponse>> = {} as Observable<RequestState<PlanTableResponse>>;
  planUnedited: PlanTableRecord[] = [];
  localEdits: PlanTableRecord[] = [];
  cacheId = CacheId.ActivePlan;
  editsCacheId = CacheId.ActivePlanEdits;
  planUnsaved: boolean = false;

  ngOnInit(): void {
    this.cacheId = this.planKey === 0 ? CacheId.ActivePlan : CacheId.StandbyPlan;
    this.editsCacheId = this.planKey === 0 ? CacheId.ActivePlanEdits : CacheId.StandbyPlanEdits;
    this.loadLocalEdits();
    this.checkForUnsavedData();
    this.getData(false);
    this.planTableService.setTableHeight(this.planKey);
  }

  loadLocalEdits(): void {
    const localEdits = localStorage.getItem(this.editsCacheId);
    if (localEdits) {
      this.localEdits = JSON.parse(localEdits);
    }
  }

  saveLocalEdits(): void {
    localStorage.setItem(this.editsCacheId, JSON.stringify(this.localEdits));
  }

  navigateToPlanMonth(record: PlanTableRecord): void {
    this.router.navigate(["/planmonth", record.key, record.effectivePeriod]);
  }

  onKeyDown(
    event: KeyboardEvent,
    record: PlanTableRecord,
    field: keyof PlanTableRecord,
    plan: PlanTableResponse
  ): void {
    formatTimeInput(event);
    const input = event.target as HTMLInputElement;
    (record[field] as string) = input.value;
    this.handleChangedRecord(record, field);
    this.plan$ = this.planTableService.recalculateImaxValues(plan);
  }

  getData(force: boolean): void {
    this.plan$ = this.planTableService.getPlanData(force, this.planKey).pipe(
      tap((response) => {
        if (response.response) {
          this.planUnedited = JSON.parse(JSON.stringify(response.response.plans));
          response.response.plans = response.response.plans.map((record) => {
            const editedRecord = this.localEdits.find((r) => r.planID === record.planID);
            return editedRecord ? { ...record, ...editedRecord } : record;
          });
        }
      })
    );
  }

  refreshData(): void {
    this.plan$ = this.planTableService.getPlanData(true, this.planKey).pipe(
      tap((response) => {
        if (response.response) {
          this.localEdits = [];
          this.saveLocalEdits();
          this.planUnedited = JSON.parse(JSON.stringify(response.response.plans));
          this.planTableService.setTableHeight(this.planKey);
          // this.globalService.checkforUnsavedData();
          this.checkForUnsavedData();
          this.planTableService.isLoading.set(false);
        }
      })
    );
  }

  handleChangedRecord(record: PlanTableRecord, field: keyof PlanTableRecord): void {
    const originalRecord = this.planUnedited.find((r) => r.planID === record.planID);
    const localEditIndex = this.localEdits.findIndex((r) => r.planID === record.planID);

    if (originalRecord && originalRecord[field] !== record[field]) {
      if (localEditIndex > -1) {
        this.localEdits[localEditIndex] = record;
      } else {
        this.localEdits.push(record);
      }
    } else if (localEditIndex > -1) {
      this.localEdits.splice(localEditIndex, 1);
    }

    this.saveLocalEdits();
    // this.globalService.checkforUnsavedData();
    this.checkForUnsavedData();
    this.planTableService.setTableHeight(this.planKey);
  }

  isCellEdited(planID: number, field: keyof PlanTableRecord, value: any): boolean {
    const originalRecord = this.planUnedited.find((r) => r.planID === planID);

    if (!originalRecord) {
      return false;
    }
    const isEditedComparedToOriginal = originalRecord[field] !== value;
    return isEditedComparedToOriginal;
  }

  revertChanges(): void {
    this.localEdits = [];
    this.saveLocalEdits();
    // this.globalService.checkforUnsavedData();
    this.checkForUnsavedData();
    this.planTableService.setTableHeight(this.planKey);
    this.getData(false);
  }

  saveChanges(plan: PlanTableRecord[]): void {
    this.planTableService
      .updatePlanRecordsInDB(plan, this.planUnedited, this.cacheId)
      .pipe(
        finalize(() => {
          this.refreshData();
        })
      )
      .subscribe();
  }

  imaxTargetReached(imax: number): boolean {
    return imax <= 900;
  }

  projExceededImax(proj: string, imax: number): boolean {
    return convertToMinutes(proj) > imax;
  }

  projExceededVmax(proj: string, vmax: string): boolean {
    return convertToMinutes(proj) > convertToMinutes(vmax);
  }

  private checkForUnsavedData(): void {
    this.globalService.checkforUnsavedData();
    if (this.localEdits.length > 0) {
      this.planUnsaved = true;
    } else {
      this.planUnsaved = false;
    }
  }
}
